import { authedFetch, applyUnauthorizedPolicy, BFF_BASE_URL, ProviderErrorResponse, UnauthorizedError } from '@apis/auth';
import type { ProviderName } from '@interfaces/IAuth';
export { ProviderErrorResponse };

export type DocumentStatus = 'draft' | 'in_review' | 'approved' | 'rejected' | 'superseded';

/** Who decided a version's fate and why — shown to its uploader. */
export interface IVersionDecision {
  by: string;
  at: string;
  comment: string;
  /** Set when this version was closed by a NEWER version being approved rather
   *  than by someone deciding on it; names that version. */
  supersededBySeq?: number;
}

export interface IDocumentVersion {
  version: number;
  description: string;
  author: string;
  date: string;
  fileId: string;
  sizeBytes: number;
  status: DocumentStatus;
  decision?: IVersionDecision;
  canSubmit: boolean;
  canApprove: boolean;
  canReject: boolean;
  canDelete: boolean;
}

export interface IFolder {
  id: string;
  name: string;
  parentId: string | null;
  isSystem?: boolean;
  saving?: boolean; // Client-only: it, or a folder above it, is being renamed or moved — its id is about to change.
}

export interface IDocument {
  id: string;
  documentId?: string; // Stable versioned-document id; absent = standalone file the version index doesn't govern.
  name: string;
  description: string;
  version: number;
  isCurrent: boolean;
  status: DocumentStatus;
  approvedVersion: number | null; // The version the field acts on; null while nothing has been approved.
  sizeBytes: number;
  updatedAt: string; // ISO timestamp
  updatedBy: string;
  folderId: string; // → IFolder.id
  versions: IDocumentVersion[];
  saving?: boolean; // Client-only: a rename of it is saving, so its id is about to change.
}

// A document id encodes its folder: "photo:aaa/file.png" lives in folder
// "photo:aaa"; a root-level "photo:/file.png" lives in "photo:/". Lets a
// deep-link build a fully-resolved URL (?folder=…&doc=…) without a round trip.
export function folderIdFromFileId(fileId: string): string | null {
  const slash = fileId.lastIndexOf('/');
  if (slash === -1) return null;
  const candidate = fileId.slice(0, slash);
  return candidate.endsWith(':') ? `${candidate}/` : candidate;
}

// Image extensions Genba's "photo" root holds — used to decide whether a
// document can be rendered inline as a preview rather than a generic icon.
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];

export function isImageDocument(doc: Pick<IDocument, 'name' | 'id'>): boolean {
  const source = (doc.name || doc.id).toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => source.endsWith(ext));
}

export function isPdfDocument(doc: Pick<IDocument, 'name' | 'id'>): boolean {
  return (doc.name || doc.id).toLowerCase().endsWith('.pdf');
}

/**
 * The file family behind a document's row icon. Extension-only — nothing on
 * the wire carries a mime type, and Genba's ids keep the extension even when
 * the display name has been renamed away from it.
 */
export type TDocumentKind = 'image' | 'pdf' | 'sheet' | 'cad' | 'doc' | 'other';

const KIND_EXTENSIONS: Array<[TDocumentKind, string[]]> = [
  ['sheet', ['.xls', '.xlsx', '.xlsm', '.csv']],
  ['cad', ['.dwg', '.dxf', '.ifc', '.rvt', '.skp', '.jww', '.sfc', '.p21']],
  ['doc', ['.doc', '.docx', '.txt', '.md', '.rtf', '.ppt', '.pptx']],
];

export function documentKind(doc: Pick<IDocument, 'name' | 'id'>): TDocumentKind {
  if (isImageDocument(doc)) return 'image';
  if (isPdfDocument(doc)) return 'pdf';
  const ext = extensionOf((doc.name || doc.id).toLowerCase());
  return KIND_EXTENSIONS.find(([, exts]) => exts.includes(ext))?.[0] ?? 'other';
}

// What the current user may do on this site's documents. `delete` is
// admin-only — the backend enforces it on the delete routes;
// `move` stays false until the upstream move API exists (drag-drop disabled).
export interface IDocumentsCapabilities {
  delete: boolean;
  move: boolean;
  approve: boolean;
}

export interface IFoldersResponse {
  folders: IFolder[];
  capabilities: IDocumentsCapabilities;
}

export interface IFilesResponse {
  documents: IDocument[];
}

async function handle(res: Response): Promise<never> {
  const body = await res.json().catch(() => null);
  throwForError(res.status, body);
}

/** A refused documents request, with the status and code that say why. */
export class DocumentsRequestError extends Error {
  constructor(public readonly status: number, public readonly code: string | undefined, message: string) {
    super(message);
    this.name = 'DocumentsRequestError';
  }
}

/**
 * Maps a failed response to the typed error the hooks expect. Split out of
 * `handle` so the XHR upload path (which has a status and a parsed body, not a
 * `Response`) reports failures identically instead of growing its own rules.
 */
function throwForError(status: number, body: ErrorBody | null): never {
  const code = body?.error?.code;
  const provider = body?.error?.details?.provider;
  if (status === 401) {
    if (code === 'PROVIDER_REAUTH_REQUIRED' && provider) throw new ProviderErrorResponse(code, provider);
    throw new UnauthorizedError();
  }
  if (status === 409 && provider && (code === 'PROVIDER_NOT_CONNECTED' || code === 'PROVIDER_DISCONNECTED')) {
    throw new ProviderErrorResponse(code, provider);
  }
  throw new DocumentsRequestError(status, code, body?.error?.message ?? `request failed (${status})`);
}

type ErrorBody = { error?: { code?: string; message?: string; details?: { provider?: ProviderName } } };

// Folder structure only — the fast initial load.
export async function fetchFolders(
  siteId: string,
  signal?: AbortSignal,
): Promise<IFoldersResponse> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return res.json() as Promise<IFoldersResponse>;
}

// Files for one folder, or every file across the site when `folderId` is omitted
export async function fetchFiles(
  siteId: string,
  folderId?: string,
  signal?: AbortSignal,
): Promise<IFilesResponse> {
  const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : '';
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/files${qs}`,
    { signal },
  );
  if (!res.ok) return handle(res);
  return res.json() as Promise<IFilesResponse>;
}

// Fetches the raw file bytes from the backend (which proxies Genba's download
// endpoint). We go through authedFetch (session cookie via credentials:'include')
// and hand back a blob so the caller can render an object URL or save to disk,
// and so auth/error handling stays uniform with the rest of the client.
export async function fetchDocumentBlob(
  siteId: string,
  fileId: string,
  signal?: AbortSignal,
): Promise<{ blob: Blob; fileName: string }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/download?fileId=${encodeURIComponent(fileId)}`,
    { signal },
  );
  if (!res.ok) return handle(res);

  // Prefer the filename the backend set in Content-Disposition; fall back to
  // the last path segment of the doc id.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  const fileName = star?.[1]
    ? decodeURIComponent(star[1])
    : plain?.[1] ?? fileId.split('/').filter(Boolean).pop() ?? 'download';

  const blob = await res.blob();
  return { blob, fileName };
}

/**
 * The file's preview image (small size), or null when there is none — the cue
 * to render the full bytes from `fetchDocumentBlob` instead.
 */
export async function fetchDocumentThumbnail(
  siteId: string,
  fileId: string,
  signal?: AbortSignal,
): Promise<Blob | null> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/thumbnail?fileId=${encodeURIComponent(fileId)}`,
    { signal },
  );
  if (res.status === 404) return null;
  if (!res.ok) return handle(res);
  return res.blob();
}

/**
 * What a downloaded version is called: the document's name, with the version
 * marked when it is not the one the document currently stands for — so three
 * downloads of the same document arrive as distinguishable files.
 *
 * Named here rather than read off the response because the download is a
 * cross-origin fetch, and `Content-Disposition` is not a header the browser
 * lets scripts read. The backend applies this same rule for direct API calls.
 */
export function downloadNameFor(displayName: string, version: number, approvedVersion: number | null): string {
  if (version === approvedVersion) return displayName;
  const ext = extensionOf(displayName);
  return `${displayName.slice(0, displayName.length - ext.length)} (v${version})${ext}`;
}

/**
 * The extension including its dot, or '' when there is none. A leading dot is
 * part of the name rather than a stem, so ".gitignore" has no extension.
 * Mirrors the backend rule, which rejects a rename that changes it.
 */
export function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot);
}

// Fetches the file and triggers a browser save via a temporary object URL.
// `saveAs` names the saved file; without it the stored name is used, which
// stops matching the document once it is renamed.
export async function downloadDocument(
  siteId: string,
  fileId: string,
  saveAs?: string,
): Promise<void> {
  const { blob, fileName: storedName } = await fetchDocumentBlob(siteId, fileId);
  const fileName = saveAs ?? storedName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Creates a subfolder under `parentId` (a system root or an existing folder).
// Returns the created folder so the caller can splice it into the tree without
// re-fetching the whole structure.
export async function createFolder(
  siteId: string,
  parentId: string,
  name: string,
): Promise<{ folder: IFolder }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/folders`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentId, name }),
    },
  );
  if (!res.ok) return handle(res);
  return res.json() as Promise<{ folder: IFolder }>;
}

// Renames a folder. Because folder ids encode their path, the returned folder
// carries a new id; callers must reconcile descendant ids (e.g. by refetching
// the folder tree) since those change too.
export async function renameFolder(
  siteId: string,
  folderId: string,
  name: string,
): Promise<{ folder: IFolder }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/folders/${encodeURIComponent(folderId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    },
  );
  if (!res.ok) return handle(res);
  return res.json() as Promise<{ folder: IFolder }>;
}

// Deletes a folder and all of its contents.
export async function deleteFolder(siteId: string, folderId: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/folders?folderId=${encodeURIComponent(folderId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) await handle(res);
}

// Renames a document.
export async function renameDocument(siteId: string, fileId: string, name: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/files/${encodeURIComponent(fileId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    },
  );
  if (!res.ok) await handle(res);
}

// Deletes a document by its id — the backend removes every version behind it.
export async function deleteDocument(siteId: string, fileId: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/files?fileId=${encodeURIComponent(fileId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) await handle(res);
}

// Moves a document into another folder — the backend relocates every version
// file behind it. Available only while capabilities.move is true (409
// MOVE_UNAVAILABLE otherwise).
export async function moveDocument(siteId: string, fileId: string, targetFolderId: string): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/files/${encodeURIComponent(fileId)}/move`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetFolderId }),
    },
  );
  if (!res.ok) await handle(res);
}

// Moves a folder (with its subtree) under another folder. Like rename, the
// returned folder carries a new path-based id; callers refetch to reconcile.
export async function moveFolder(siteId: string, folderId: string, targetFolderId: string): Promise<{ folder: IFolder }> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/folders/${encodeURIComponent(folderId)}/move`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetFolderId }),
    },
  );
  if (!res.ok) return handle(res);
  return res.json() as Promise<{ folder: IFolder }>;
}

// Deletes a single version from a document's history (admin-only, like all
// deletes). Other versions keep their numbers — nothing is renumbered.
export async function deleteVersion(siteId: string, documentId: string, seq: number): Promise<void> {
  const res = await authedFetch(
    `/api/sites/${encodeURIComponent(siteId)}/documents/files/version?documentId=${encodeURIComponent(documentId)}&seq=${seq}`,
    { method: 'DELETE' },
  );
  if (!res.ok) await handle(res);
}

// The URL of one version of one document — every version action hangs off it,
// with the action as the last segment.
function versionUrl(siteId: string, documentId: string, seq: number): string {
  return `/api/sites/${encodeURIComponent(siteId)}/documents/files/${encodeURIComponent(documentId)}/versions/${seq}`;
}

// Puts one's own draft in front of an approver. Only the version's uploader
// may — the backend answers 404 for a draft the caller cannot see.
export async function submitVersion(siteId: string, documentId: string, seq: number): Promise<void> {
  const res = await authedFetch(`${versionUrl(siteId, documentId, seq)}/submit`, { method: 'POST' });
  if (!res.ok) await handle(res);
}

// Approves or rejects the version awaiting a decision (admin-only) — the
// outcome is the route, not a field. A comment is required to reject: it is the
// only feedback the uploader receives.
export async function decideVersion(
  siteId: string,
  documentId: string,
  seq: number,
  decision: 'approve' | 'reject',
  comment?: string,
): Promise<void> {
  const res = await authedFetch(
    `${versionUrl(siteId, documentId, seq)}/${decision}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...(comment ? { comment } : {}) }),
    },
  );
  if (!res.ok) await handle(res);
}

/**
 * Uploads via XMLHttpRequest rather than `authedFetch`, because fetch cannot
 * report upload progress and a 100 MB drawing on a site connection needs to show
 * one. `withCredentials` mirrors fetch's `credentials: 'include'`, and the 401
 * policy comes from `applyUnauthorizedPolicy` so the two transports can't drift.
 *
 * `onProgress` receives 0–1, and only while the total is known — a request with
 * no computable length reports nothing rather than a fabricated number.
 *
 * `signal` cancels the in-flight request; the promise then rejects with a
 * DOMException named `AbortError`, matching fetch's cancellation contract.
 */
export interface IUploadDocumentOptions {
  comment?: string;     // Version comment, shown in the document's history.
  documentId?: string;  // Append to this document as its next version
  versionOf?: string;   // The same for a file not versioned yet (no documentId): it is adopted first
  submitForApproval?: boolean;
  approve?: boolean;    // Approve in the same request
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export interface IUploadResult {
  // The STORED name — the backend may rename on a collision. fileId addresses
  // the stored file (e.g. to select it after the upload).
  fileName: string;
  fileId: string;
  // Whether the requested `approve` actually took effect. False when it wasn't
  // asked for, and ALSO when the approval rules refused it (an older version
  // still awaiting a decision) — the upload succeeded either way, so a caller
  // that asked to approve has to read this to know the version isn't published.
  approved: boolean;
  // The name the document shows: the file's own, or — when a document in the
  // folder already has it — the next free one ("plan (2).pdf").
  name: string;
}

export function uploadDocument(
  siteId: string,
  folderId: string,
  file: File,
  { comment, documentId, versionOf, submitForApproval, approve, onProgress, signal }: IUploadDocumentOptions = {},
): Promise<IUploadResult> {
  const form = new FormData();
  form.append('folderId', folderId);
  if (documentId) form.append('documentId', documentId);
  if (versionOf) form.append('versionOf', versionOf);
  if (submitForApproval === false) form.append('submitForApproval', 'false');
  if (approve) form.append('approve', 'true');
  form.append('file', file);
  if (comment) form.append('comment', comment);

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('upload aborted', 'AbortError'));
      return;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BFF_BASE_URL}/api/sites/${encodeURIComponent(siteId)}/documents/upload`);
    xhr.withCredentials = true;

    // Aborting a finished XHR is a spec'd no-op, so no listener cleanup needed.
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      });
    }

    xhr.addEventListener('error', () => reject(new Error('upload failed')));
    xhr.addEventListener('abort', () => reject(new DOMException('upload aborted', 'AbortError')));
    xhr.addEventListener('load', () => {
      const body = parseJson(xhr.responseText);
      if (xhr.status === 401) applyUnauthorizedPolicy(body?.error?.code);
      if (xhr.status < 200 || xhr.status >= 300) {
        try { throwForError(xhr.status, body); } catch (err) { reject(err); }
        return;
      }
      if (!isUploadResult(body)) {
        reject(new Error('upload succeeded but the response was not the expected shape'));
        return;
      }
      resolve(body);
    });

    xhr.send(form);
  });
}

function isUploadResult(v: unknown): v is IUploadResult {
  return (
    typeof v === 'object' && v !== null &&
    typeof (v as Record<string, unknown>).fileName === 'string' &&
    typeof (v as Record<string, unknown>).fileId === 'string' &&
    typeof (v as Record<string, unknown>).approved === 'boolean' &&
    typeof (v as Record<string, unknown>).name === 'string'
  );
}

function parseJson(text: string): ErrorBody | null {
  try { return JSON.parse(text) as ErrorBody; } catch { return null; }
}